import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RateLimitResponse {
  allowed: boolean;
  blocked: boolean;
  remaining_attempts?: number;
  retry_after_seconds?: number;
  message?: string;
}

interface RateLimitState {
  isBlocked: boolean;
  retryAfterSeconds: number | null;
  remainingAttempts: number | null;
  message: string | null;
}

export function useRateLimit() {
  const [state, setState] = useState<RateLimitState>({
    isBlocked: false,
    retryAfterSeconds: null,
    remainingAttempts: null,
    message: null,
  });

  const checkRateLimit = useCallback(async (
    identifier: string,
    actionType: "login" | "signup" | "password_reset" | "resend_confirmation"
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke<RateLimitResponse>(
        "check-rate-limit",
        {
          body: { identifier, action_type: actionType },
        }
      );

      if (error) {
        console.error("Rate limit check error:", error);
        // On error, allow the action (fail open)
        return true;
      }

      if (data) {
        setState({
          isBlocked: data.blocked,
          retryAfterSeconds: data.retry_after_seconds ?? null,
          remainingAttempts: data.remaining_attempts ?? null,
          message: data.message ?? null,
        });

        return data.allowed;
      }

      return true;
    } catch (error) {
      console.error("Rate limit check failed:", error);
      // On error, allow the action (fail open)
      return true;
    }
  }, []);

  const resetState = useCallback(() => {
    setState({
      isBlocked: false,
      retryAfterSeconds: null,
      remainingAttempts: null,
      message: null,
    });
  }, []);

  return {
    ...state,
    checkRateLimit,
    resetState,
  };
}
